import Service, { service } from '@ember/service';

import type { IntlService } from 'ember-intl';
import type { IJSONAPIErrors } from 'volta/utils/error-utils';
import type { SafeString } from '@ember/template';
import type { FormatMessageParameters } from 'ember-intl/-private/formatjs';

type IUnitMeasures =
  | 'CENTIMETRE'
  | 'CUBE_CENTIMETRE'
  | 'CUBE_METRE'
  | 'DAY'
  | 'GRAM'
  | 'HOUR'
  | 'KG'
  | 'KILOGRAM'
  | 'LITRE'
  | 'LT'
  | 'METRE'
  | 'MILLIGRAM'
  | 'MILLILITER'
  | 'MILLIMETRE'
  | 'MINUTE'
  | 'MT'
  | 'PIECE'
  | 'SECOND'
  | 'SQUARE_CENTIMETRE'
  | 'SQUARE_METRE'
  | 'SQUARE_MILLIMETRE'
  | 'TICKET'
  | 'UN'
  | 'UNIT'
  | 'UNKNOWN';

export default class LabelService extends Service {
  // Services
  // ~~~~~

  @service declare intl: IntlService;

  /**
   * Given a translation key, this function will check if the corresponding label key exists
   * if it exists, it will return the label translation otherwise it will simply format the key and return it
   * @param translation Translation key
   * @param fallback Fallback value
   * @param options Options
   */
  tOrElse(
    translation: string | SafeString,
    fallback?: string | SafeString,
    options?: FormatMessageParameters[1] & {
      htmlSafe?: boolean;
      locale?: string;
    }
  ): string | SafeString {
    return (
      this.intl.exists(translation as string)
        ? this.intl.t(translation as string, options)
        : (fallback ?? translation)
    ) as string | SafeString;
  }

  /**
   * Given the unit measure, this function will check if the corresponding label key exists
   * if it exists, it will return the label translation otherwise it will simply format the key and return it
   *
   * @param {string|null} unit Unit measure
   * @returns {string} Translated Unit Measure, or formatted key if custom
   */
  unitMeasure(unit: string | IUnitMeasures | undefined): string {
    if (!unit) {
      return '';
    }

    const intl = this.intl;
    const key = `unitMeasures.${unit}`;

    if (intl.exists(key)) {
      return this.intl.t(key);
    } else {
      return unit;
    }
  }

  /**
   *  Transforms a JSONAPI error into a structure suitable for form validation
   *  @example
   *    ```javascript
   *      let error = {
   *         errors: [
   *           title: 'some.label.key',
   *         detail: {
   *           description: 'someField',
   *             otherArg: 'otherArgValue'
   *           }
   *         ]
   *       };
   *   ```
   *  @example
   *  let formError = this.labelService.formValidationsFromApiError(error);
   *  formError = { someField: ['Translated error description'] };
   *
   *  @param {Object} error JSONAPI error
   *  @returns {Object<string,string[]>|null} a form error structure if there are any errors, otherwise `null`
   */

  formValidationsFromApiError(error: IJSONAPIErrors): { [s: string]: string[] } | null {
    const { errors = [] } = error;
    const formErrors: { description?: string[] } = {};
    errors.forEach((err) => {
      const { title, detail = {} } = err;
      const description = detail.description ?? 'default';
      if (description) {
        const newError = this.intl.t(title, {
          ...detail,
          description: this.intl.t(description)
        });
        const oldErrors: string[] = formErrors[description as keyof typeof formErrors] ?? [];
        oldErrors.push(newError);
        formErrors[description as keyof typeof formErrors] = oldErrors;
      }
    });
    return Object.keys(formErrors).length > 0 ? formErrors : null;
  }
}

declare module '@ember/service' {
  interface Registry {
    'label-service': LabelService;
  }
}
